import { Box, Button, Card, Grid, TextField, Typography } from "@mui/material"
import { useRef } from "react"
import { useQuery } from "@blitzjs/rpc"
import SearchIcon from "@mui/icons-material/Search"
import { DateRangePicker } from "app/core/components/DateRangePicker"
import { OptionsFilter } from "app/core/components/OptionsFilter"
import { format } from "date-fns"
import { DATE_FORMAT } from "app/date"
import { Prisma } from "db"
import getAbExperimentFilterData from "app/abExperiments/queries/getAbExperimentFilterData"

export type QueryParams = {
  orderBy: {
    id?: "asc" | "desc"
    createdAt?: "asc" | "desc"
  }
  where: {
    url?: {
      contains: string
      mode: "insensitive"
    }
    brandId?: number
    geographyId?: number | { in: number[] }
    languageId?: number | { in: number[] }
    status?: string
  }
  resultDateFrom?: Date
  resultDateTill?: Date
}

type AbExperimentControlsProps = {
  queryParams: QueryParams
  setQueryParams: (queryParams: QueryParams) => any
}

const AbExperimentControls = ({ queryParams, setQueryParams }: AbExperimentControlsProps) => {
  const [filterData] = useQuery(getAbExperimentFilterData, undefined, { suspense: false })
  const nameSearchQuery = useRef<string>("")

  const handleDatesChange = ({ startDate, endDate }) => {
    setQueryParams({
      ...queryParams,
      resultDateFrom: startDate,
      resultDateTill: endDate,
    })
  }

  const handleNameSearch = () => {
    const searchTerm = nameSearchQuery.current.trim()
    const newWhere = { ...queryParams.where }

    if (searchTerm) {
      newWhere.url = { contains: searchTerm, mode: "insensitive" }
    } else {
      delete newWhere.url
    }

    setQueryParams({
      ...queryParams,
      where: newWhere,
    })
  }

  const handleQueryParamsWhereChange = (newValueFn: Function) => {
    const newValue = newValueFn()

    setQueryParams({
      ...queryParams,
      where: {
        ...queryParams.where,
        ...newValue,
      },
    })
  }

  // Status options for the filter
  const statusOptions = [
    { id: "active", name: "Active" },
    { id: "completed", name: "Completed" },
  ]

  return (
    <>
      <Card sx={{ mb: 4, p: 2 }}>
        <Grid container alignItems="center" my={0.5}>
          <Grid item mr={2} width={140}>
            <Typography variant="subtitle1">Dates</Typography>
          </Grid>

          <Grid item>
            <DateRangePicker
              values={{
                startDate: queryParams.resultDateFrom,
                endDate: queryParams.resultDateTill,
              }}
              onChange={handleDatesChange}
            />
          </Grid>
        </Grid>

        <Box sx={{ my: 1.5 }}>
          <OptionsFilter
            title="Brand"
            collection={filterData?.brands || []}
            optionValue={(brand: any) => brand.id}
            optionLabel={(brand: any) => brand.name}
            field="brandId"
            search={queryParams.where}
            onSearch={handleQueryParamsWhereChange}
          />
        </Box>
        <Box sx={{ my: 1.5 }}>
          <OptionsFilter
            title="Geography"
            collection={filterData?.geographies || []}
            optionValue={(geography: any) => geography.id}
            optionLabel={(geography: any) => geography.name}
            field="geographyId"
            search={queryParams.where}
            onSearch={handleQueryParamsWhereChange}
          />
        </Box>
        <Box sx={{ my: 1.5 }}>
          <OptionsFilter
            title="Language"
            collection={filterData?.languages || []}
            optionValue={(language: any) => language.id}
            optionLabel={(language: any) => language.code}
            field="languageId"
            search={queryParams.where}
            onSearch={handleQueryParamsWhereChange}
          />
        </Box>
        <Box sx={{ my: 1.5 }}>
          <OptionsFilter
            title="Status"
            collection={statusOptions}
            optionValue={(status: any) => status.id}
            optionLabel={(status: any) => status.name}
            field="status"
            search={queryParams.where}
            onSearch={handleQueryParamsWhereChange}
          />
        </Box>

        <Grid container alignItems="center" mt={2.5} mb={0.5}>
          <Grid item mr={2} width={140}>
            <Typography variant="subtitle1">Filters</Typography>
          </Grid>

          <Grid item>
            <Box sx={{ display: "flex", flexDirection: { md: "row", xs: "column" }, gap: 2 }}>
              <TextField
                size="small"
                label="URL"
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                InputProps={{ endAdornment: <SearchIcon sx={{ ml: 1 }} color="action" /> }}
                onChange={(e) => (nameSearchQuery.current = e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleNameSearch()
                }}
              />
              <Button variant="outlined" onClick={handleNameSearch}>
                Search
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>
    </>
  )
}

export default AbExperimentControls
